import {useState, useEffect} from "react";
import {useEditor, EditorContent} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {postToApi, patchToApi} from "../../api/auth";

function MenuBar({editor}) {
    if (!editor) return null;

    const btn = (label, action, active) => (
        <button
            type="button"
            onMouseDown={e => { e.preventDefault(); action(); }}
            className={`tiptap-btn ${active ? "is-active" : ""}`}
            title={label}
        >
            {label}
        </button>
    );

    return (
        <div className="tiptap-toolbar">
            {btn("B", () => editor.chain().focus().toggleBold().run(), editor.isActive("bold"))}
            {btn("I", () => editor.chain().focus().toggleItalic().run(), editor.isActive("italic"))}
            {btn("S", () => editor.chain().focus().toggleStrike().run(), editor.isActive("strike"))}
            <span className="tiptap-divider" />
            {btn("H1", () => editor.chain().focus().toggleHeading({level: 1}).run(), editor.isActive("heading", {level: 1}))}
            {btn("H2", () => editor.chain().focus().toggleHeading({level: 2}).run(), editor.isActive("heading", {level: 2}))}
            {btn("H3", () => editor.chain().focus().toggleHeading({level: 3}).run(), editor.isActive("heading", {level: 3}))}
            <span className="tiptap-divider" />
            {btn("• List", () => editor.chain().focus().toggleBulletList().run(), editor.isActive("bulletList"))}
            {btn("1. List", () => editor.chain().focus().toggleOrderedList().run(), editor.isActive("orderedList"))}
            {btn("❝", () => editor.chain().focus().toggleBlockquote().run(), editor.isActive("blockquote"))}
            <span className="tiptap-divider" />
            {btn("↩", () => editor.chain().focus().undo().run())}
            {btn("↪", () => editor.chain().focus().redo().run())}
        </div>
    );
}

export default function AddNote({folderId, onSaved, onCancel, entryId, initialTitle = "", initialContent = ""}) {
    const isEdit = Boolean(entryId);
    const [title, setTitle] = useState(initialTitle);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const editor = useEditor({
        extensions: [StarterKit],
        content: initialContent || "",
        editorProps: {
            attributes: {class: "tiptap-editor"},
        },
    });

    useEffect(() => {
        if (editor && initialContent) {
            editor.commands.setContent(initialContent);
        }
    }, [initialContent, editor]);

    const handleSave = async () => {
        const content = editor?.getHTML();
        setSaving(true);
        setError("");
        try {
            if (isEdit) {
                await patchToApi(`api/notes/entry/${entryId}`, {title: title.trim() || "Untitled", content});
            } else {
                await postToApi(`api/notes/${folderId}/entry`, {title: title.trim() || "Untitled", content});
            }
            onSaved?.();
        } catch {
            setError("Failed to save. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <input
                className="form-control border-0 border-bottom rounded-0 px-0 fw-semibold mb-3"
                placeholder="Note title..."
                value={title}
                onChange={e => setTitle(e.target.value)}
                style={{boxShadow: "none", fontSize: "1.1rem"}}
            />

            <MenuBar editor={editor} />
            <EditorContent editor={editor} />

            {error && <div className="text-danger small mt-2">{error}</div>}

            <div className="d-flex gap-2 mt-3">
                <button className="btn btn-dark btn-sm" onClick={handleSave} disabled={saving}>
                    <i className={`bi bi-${isEdit ? "floppy" : "plus-lg"} me-1`}/>
                    {saving ? "Saving..." : isEdit ? "Save changes" : "Add note"}
                </button>
                {onCancel && (
                    <button className="btn btn-outline-secondary btn-sm" onClick={onCancel} disabled={saving}>Cancel</button>
                )}
            </div>
        </div>
    );
}
